import axios from 'axios';
import { Logger } from '../Logger';
import { RetailerPersonalData } from '../../common-types/GlobalRetailerPersonalDataState.type';
import { OptinsDataFromSu } from '../../common-types/OptinsDataFromSu.type';

class PersonalDataService {
  private readonly host: string;
  private readonly disabled: boolean;
  private readonly SU_SITE_KEY = 'nhQxzLVX';
  private readonly SU_API_BASE_URL =
    'https://ext-api.systeme-u.com/espaceclient-comptes-process-api/v1/espaceclient';

  public constructor() {
    this.host = process.env.REACT_APP_PERSONAL_DATA_BASE_URL as string;
    this.disabled =
      typeof this.host !== 'string' || this.host.trim().length === 0;
  }

  // eslint-disable-next-line class-methods-use-this
  public async getOptinsFromSuServer(token: string): Promise<OptinsDataFromSu> {
    // MOCK RESPONSE TO TEST SU IN LOCAL
    // return {
    //   nom: 'Jean Dupont',
    //   prenom: 'Dupont',
    //   civilite: 'Monsieur',
    //   email: 'adamkrim-ext@luckycart.com',
    //   numeroCarteU: '123456789',
    //   telephonePrincipal: '0612345678',
    //   allowsEmailCommunication: true,
    //   allowsSMSCommunication: true,
    // } as OptinsDataFromSu;

    const data = await fetch(`${this.SU_API_BASE_URL}/v2/compte`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => Promise.all([response, response.json()]))
      .then(([response, json]) => {
        if (!response.ok) {
          throw new Error(`Erreur HTTP, statut : ${response.status}`);
        }
        return json as unknown as OptinsDataFromSu;
      })
      .catch((error) => {
        console.log(error);
        console.error('Erreur lors de la requête :', error);
        return {} as OptinsDataFromSu;
      });

    return data;
  }

  // eslint-disable-next-line class-methods-use-this
  public updateOptinsToSuServer(
    token: string,
    consents: Record<string, boolean>
  ): Promise<Response> {
    // MOCK RESPONSE TO TEST SU IN LOCAL
    // return new Promise((resolve, reject) => {
    //   resolve(new Response());
    // });

    const consentsToUpdate = {
      allowEmail: consents.allowsEmailCommunication ?? false,
      allowSms: consents.allowsSMSCommunication ?? false,
    };

    if (
      consentsToUpdate.allowEmail === false &&
      consentsToUpdate.allowSms === false
    ) {
      console.error('No consents to update', consents);
      return Promise.resolve(new Response());
    }

    const url = `${this.SU_API_BASE_URL}/compte/allowsCommunication`;

    return fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(consentsToUpdate),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Erreur HTTP, statut : ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.log(error);
        console.error('Erreur lors de la requête :', error);
      });
  }

  public async getCustomerPersonalData(
    siteKey: string,
    customerUid: string,
    token: string | null = null
  ): Promise<RetailerPersonalData> {
    if (!['Dwj83zVC', 'nhQxzLVX'].includes(siteKey)) {
      return {
        optinList: [],
        email: null,
        phone: null,
      };
    }
    const personalData: RetailerPersonalData = {
      optinList: [],
      email: null,
      phone: null,
    };
    if (token !== null) {
      let retailerData: unknown = {};
      try {
        retailerData = await this.getOptinsFromSuServer(token);
      } catch (error) {
        return personalData;
      }
      const {
        email,
        allowsEmailCommunication,
        allowsSMSCommunication,
        telephonePrincipal,
      } = retailerData as OptinsDataFromSu;
      personalData.email = email ?? null;
      if (!allowsEmailCommunication && email) {
        personalData.optinList = [
          { name: 'email', key: 'email', optinKey: 'allowsEmailCommunication' },
        ];
      } else if (
        allowsEmailCommunication &&
        !allowsSMSCommunication &&
        telephonePrincipal !== undefined
      ) {
        personalData.optinList = [
          { name: 'sms', key: 'sms', optinKey: 'allowsSMSCommunication' },
        ];
        personalData.phone = telephonePrincipal;
      }

      return personalData;
    }
    const url = `${this.host}/v1/site/${siteKey}/customer/${customerUid}/data`;
    // Avoid fucked up call 'til we defined and mount a host for proper backend
    if (this.disabled) {
      return personalData;
    }
    try {
      // Set a timeout of 2 seconds to avoid waiting too long when this go in production without the backend
      // I bet it will happen...
      const { data } = await axios.get(url, { timeout: 2000 });
      if (Array.isArray(data?.optinList)) {
        personalData.optinList = data.optinList;
      }
      if (typeof data?.email === 'string' && data.email.trim().length > 0) {
        personalData.email = data.email.trim();
      }
    } catch (error) {
      Logger.error('could not get CustomerPersonalData', error);
    }
    return personalData;
  }

  public async saveOptins(
    siteKey: string,
    customerUid: string,
    consents: Record<string, boolean>,
    token: string
  ): Promise<void> {
    if (siteKey === this.SU_SITE_KEY && token.length) {
      this.updateOptinsToSuServer(token, consents);
      return;
    }
    // Avoid fucked up call 'til we defined and mount a host for proper backend
    if (this.disabled) {
      return;
    }
    const url = `${this.host}/v1/site/${siteKey}/customer/${customerUid}/optins`;
    const data = {
      optinList: Object.entries(consents).map(([key, value]) => ({
        key,
        value,
      })),
    };
    if (data.optinList.length === 0) {
      return;
    }
    try {
      // Set a timeout of 2 seconds to avoid waiting too long when this go in production without the backend
      // I bet it will happen...
      await axios.post(url, data, { timeout: 2000 });
    } catch (error) {
      Logger.error('could not get save consents', error);
    }
  }
}

export const personalDataServiceInstance = new PersonalDataService();
